import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  History, Loader2, RefreshCw, Mail, MessageSquare, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronRight, CircleDashed,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { formatDateTime } from "../../lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGER_LABEL = { scheduled: "Scheduled", manual: "Manual", test: "Test" };

export default function DigestSendLog() {
  const [loading, setLoading] = useState(true);
  const [digests, setDigests] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/digests/send-log`);
      setDigests(r.data.digests || []);
    } catch {
      toast.error("Failed to load digest send log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#59554D]" data-testid="digest-log-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading send log…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="digest-log-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
            <History className="w-5 h-5 text-[#8A6D00]" /> Digest Send Log
          </h3>
          <p className="text-sm text-[#59554D] mt-1 max-w-2xl">
            When each scheduled digest last went out and how many times it fired in the
            last 24 hours. A count above <b>1</b> for the same day flags a possible duplicate.
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={load}
          className="border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A] shrink-0"
          data-testid="digest-log-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="space-y-2">
        {digests.map((d) => {
          const dup = (d.sends_last_window || 0) > 1;
          const once = (d.sends_last_window || 0) === 1;
          const isOpen = !!expanded[d.key];
          return (
            <Card key={d.key} className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid={`digest-log-row-${d.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    {d.channel === "teams"
                      ? <MessageSquare className="w-4 h-4 text-[#1D4ED8] shrink-0" />
                      : <Mail className="w-4 h-4 text-[#8A6D00] shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1A1A1A] truncate">{d.label}</div>
                      <div className="text-xs text-[#8A857A]">
                        {d.last_at
                          ? <>Last sent {formatDateTime(d.last_at)}
                              {d.last_trigger && <> · {TRIGGER_LABEL[d.last_trigger] || d.last_trigger}</>}
                              {typeof d.last_recipients === "number" && <> · {d.last_recipients} recipient{d.last_recipients === 1 ? "" : "s"}</>}
                            </>
                          : "Never sent yet"}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {dup ? (
                      <Badge className="bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1]" data-testid={`digest-log-count-${d.key}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> {d.sends_last_window}× today
                      </Badge>
                    ) : once ? (
                      <Badge className="bg-[#E3F3E9] text-[#0E6B3F] border-[#E3F3E9]" data-testid={`digest-log-count-${d.key}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> 1× today
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-[#DAD5CA] text-[#8A857A]" data-testid={`digest-log-count-${d.key}`}>
                        <CircleDashed className="w-3 h-3 mr-1" /> none today
                      </Badge>
                    )}
                    {d.recent && d.recent.length > 0 && (
                      <Button
                        variant="ghost" size="sm" onClick={() => toggle(d.key)}
                        className="text-[#59554D] hover:text-[#1A1A1A] h-8 px-2"
                        data-testid={`digest-log-toggle-${d.key}`}
                      >
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <span className="ml-1 text-xs">History</span>
                      </Button>
                    )}
                  </div>
                </div>

                {isOpen && d.recent && d.recent.length > 0 && (
                  <div className="mt-3 border-t border-[#DAD5CA] pt-3" data-testid={`digest-log-history-${d.key}`}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[#8A857A] text-left">
                          <th className="py-1 pr-3 font-medium">When</th>
                          <th className="py-1 pr-3 font-medium">Trigger</th>
                          <th className="py-1 pr-3 font-medium">Recipients</th>
                          <th className="py-1 font-medium">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.recent.map((r, i) => (
                          <tr key={i} className="text-[#59554D] border-t border-[#DAD5CA]">
                            <td className="py-1.5 pr-3 whitespace-nowrap">{formatDateTime(r.at)}</td>
                            <td className="py-1.5 pr-3">{TRIGGER_LABEL[r.trigger] || r.trigger || "—"}</td>
                            <td className="py-1.5 pr-3">{typeof r.recipients === "number" ? r.recipients : "—"}</td>
                            <td className="py-1.5 text-[#59554D]">
                              {r.status === "empty" ? "Nothing to report" : (r.detail || (r.mode ? r.mode : "—"))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
